/**
 * Mock Agents Protocol Client
 * Implements IAgentsClient interface against local mock storage.
 */

import { ApiEnvelope, AgentSession } from '../../domain/types';
import { AGENTS } from '../../data/initialData';
import { IAgentsClient, ClientConfig } from './types';
import { simulateNetwork, DEFAULT_CONFIG } from './mockClientUtils';

export class MockAgentsClient implements IAgentsClient {
  private sessions: AgentSession[];
  private config: ClientConfig;

  constructor(config: ClientConfig = DEFAULT_CONFIG, includeFixtures = true) {
    this.config = config;

    this.sessions = includeFixtures
      ? AGENTS.map((a, idx) => ({
          sessionId: `sess-${idx + 1}`,
          agentName: a.name,
          state: a.running ? 'corriendo' : 'en pausa',
          running: a.running,
          targetTicketKey: a.ticket,
          workingBranch: a.branch,
          lastActivitySummary: a.last,
          elapsedSeconds: a.running ? 720 : 7800,
          elapsedFormatted: a.elapsed,
          tokensConsumed: a.name === 'Claude Code' ? 184000 : a.name === 'Codex' ? 61000 : 298000,
          tokensFormatted: a.tokens,
          costUsd: a.name === 'Claude Code' ? 2.4 : a.name === 'Codex' ? 0.85 : 3.95,
          costFormatted: a.cost,
          startedAt: '2026-08-14T09:00:00.000Z',
          updatedAt: '2026-08-14T09:15:00.000Z',
        }))
      : [];
  }

  async getSessions(): Promise<ApiEnvelope<AgentSession[]>> {
    return simulateNetwork(() => [...this.sessions], this.config);
  }

  async getSession(sessionId: string): Promise<ApiEnvelope<AgentSession | null>> {
    return simulateNetwork(() => {
      const match = this.sessions.find(
        (s) => s.sessionId === sessionId || s.agentName === sessionId,
      );
      return match ? { ...match } : null;
    }, this.config);
  }

  async toggleAgentRunning(agentName: string): Promise<ApiEnvelope<AgentSession>> {
    return simulateNetwork(() => {
      const session = this.sessions.find((s) => s.agentName === agentName);
      if (!session) {
        throw new Error(`Agent session for ${agentName} not found`);
      }
      session.running = !session.running;
      session.state = session.running ? 'corriendo' : 'en pausa';
      session.updatedAt = new Date().toISOString();
      return { ...session };
    }, this.config);
  }

  async launchAgentTask(agentName: string, taskPrompt: string): Promise<ApiEnvelope<AgentSession>> {
    return simulateNetwork(() => {
      const session = this.sessions.find((s) => s.agentName === agentName);
      if (!session) {
        throw new Error(`Agent session for ${agentName} not found`);
      }
      const addedTokens = Math.floor(Math.random() * 8000) + 12000;
      const currentTokens = session.tokensConsumed || 100000;
      const newTokens = currentTokens + addedTokens;
      const addedCost = (addedTokens / 1000) * 0.015;
      const currentCost = session.costUsd || 1.5;
      const newCost = currentCost + addedCost;

      session.tokensConsumed = newTokens;
      session.tokensFormatted = `${Math.round(newTokens / 1000)}k tok`;
      session.costUsd = newCost;
      session.costFormatted = `$${newCost.toFixed(2)}`;
      session.running = true;
      session.state = 'corriendo';
      session.lastActivitySummary = `Ejecutando: ${taskPrompt.trim()}`;
      session.updatedAt = new Date().toISOString();

      const timeStr = new Date().toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const currentLogs = session.logs || [];
      session.logs = [
        ...currentLogs,
        {
          id: `log-${Date.now()}-1`,
          timestamp: timeStr,
          level: 'cmd',
          message: `> Tarea: "${taskPrompt.trim()}"`,
        },
        {
          id: `log-${Date.now()}-2`,
          timestamp: timeStr,
          level: 'info',
          message: `Analizando codebase y ejecutando pipeline en rama ${session.workingBranch || 'feature'}...`,
        },
        {
          id: `log-${Date.now()}-3`,
          timestamp: timeStr,
          level: 'success',
          message: `+${addedTokens.toLocaleString()} tokens consumidos. Ejecución en curso.`,
        },
      ];

      return { ...session };
    }, this.config);
  }
}
